import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { type ReactNode } from 'react';
import { AdminClientsList } from './routes/AdminClientsList';
import { AdminStatus } from './routes/AdminStatus';
import { AdminClientView } from './routes/AdminClientView';
import { AdminUsers } from './routes/AdminUsers';
import { AdminGbpManagement } from './routes/AdminGbpManagement';
import { AdminIntake } from './routes/AdminIntake';
import { PublicShareView } from './routes/PublicShareView';
import { QuickAudit } from './routes/QuickAudit';
import { QuickAuditFull } from './routes/QuickAuditFull';
import { Login } from './routes/Login';
import { AuthProvider, useAuth } from './lib/auth';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { T, sharedStyles } from './components/Shared';
import type { Role, PageId } from './types';

// Wrapper that requires a signed-in user and (optionally) a role allowlist.
function RequireAuth({ children, roles }: { children: ReactNode; roles?: Role[] }) {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <FullPageMessage title="Loading…" />;
  if (status === 'signed-out' || !user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (roles && !roles.includes(user.role)) {
    return <FullPageMessage title="Access denied" detail="Your account doesn't have permission to view this page." />;
  }
  return <>{children}</>;
}

// Stricter wrapper: requires sign-in AND the page-level permission. A `client`
// is never allowed regardless of allowedPages. Used to fully isolate the
// Client Intake workspace — clients/unauthorized staff get "Access denied"
// and the route content never renders for them.
function RequirePage({ children, page }: { children: ReactNode; page: PageId }) {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <FullPageMessage title="Loading…" />;
  if (status === 'signed-out' || !user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (user.role === 'client' || !user.allowedPages.includes(page)) {
    return <FullPageMessage title="Access denied" detail="Your account doesn't have permission to view this page." />;
  }
  return <>{children}</>;
}

// For client users, /admin redirects them straight to their assigned client dashboard.
function AdminRoot() {
  const { user } = useAuth();
  if (user?.role === 'client' && user.clientId) {
    return <Navigate to={`/admin/clients/${user.clientId}`} replace />;
  }
  return <AdminClientsList />;
}

function LegacyGbpRedirect({ tab }: { tab: 'accounts' | 'locations' }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('tab', tab);
  return <Navigate to={`/admin/gbp?${params.toString()}`} replace />;
}

function FullPageMessage({ title, detail }: { title: string; detail?: string }) {
  return (
    <div style={{
      minHeight: '100vh', background: '#f8f9fa',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div style={{ textAlign: 'center', maxWidth: 420 }}>
        <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 22, fontWeight: 700, color: T.fgPrimary, marginBottom: 8 }}>
          {title}
        </div>
        {detail && <div style={{ fontSize: 14, color: T.fgSecondary, lineHeight: 1.5 }}>{detail}</div>}
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ImpersonationBanner />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/share/:token" element={<PublicShareView />} />

        {/* Admin-only — each scan spends Local Falcon credits */}
        <Route
          path="/audit"
          element={<RequireAuth roles={['admin']}><QuickAudit /></RequireAuth>}
        />

        {/* Multi-engine Quick Audit for prospects (Maps + AI + GBP) — admin + manager */}
        <Route
          path="/quick-audit"
          element={<RequireAuth roles={['admin', 'manager']}><QuickAuditFull /></RequireAuth>}
        />

        {/* Authed routes */}
        <Route path="/" element={<Navigate to="/admin" replace />} />
        <Route
          path="/admin"
          element={<RequireAuth><AdminRoot /></RequireAuth>}
        />
        <Route
          path="/admin/clients/:id"
          element={<RequireAuth><AdminClientView /></RequireAuth>}
        />
        <Route
          path="/admin/users"
          element={<RequireAuth roles={['admin']}><AdminUsers /></RequireAuth>}
        />
        <Route
          path="/admin/gbp"
          element={<RequireAuth roles={['admin', 'manager', 'staff']}><AdminGbpManagement /></RequireAuth>}
        />
        <Route
          path="/admin/integrations"
          element={<RequireAuth roles={['admin', 'manager', 'staff']}><LegacyGbpRedirect tab="accounts" /></RequireAuth>}
        />
        <Route
          path="/admin/gbp-access"
          element={<RequireAuth roles={['admin', 'manager', 'staff']}><LegacyGbpRedirect tab="locations" /></RequireAuth>}
        />
        <Route
          path="/admin/intake"
          element={<RequirePage page="intake"><AdminIntake /></RequirePage>}
        />
        <Route
          path="/admin/status"
          element={<RequireAuth roles={['admin', 'manager']}><AdminStatus /></RequireAuth>}
        />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </AuthProvider>
  );
}
